import { useEffect, useRef, useState, type ReactNode } from 'react';

import {
  captionsPlayerVars,
  captionsPrefEnabled,
  captionsPrefStored,
  setCaptionsPref,
} from '@/lib/captions';
import {
  normalizeSpeed,
  setSpeedPref,
  speedLabel,
  speedPref,
  speedPrefStored,
  SPEEDS,
  type Speed,
} from '@/lib/playbackRate';
import { cn } from '@/lib/utils';
import { PANEL_SHOW_MS, frameTap } from '@/lib/playerPanel';
import { SEEK_STEP_S, seekBy, seekFrom } from '@/lib/playerSeek';
import { useContainerFullscreen } from '@/hooks/useContainerFullscreen';
import { loadYouTubeIframeApi, type YTPlayer, type YTPlayerEvent } from '@/lib/youtube';

/**
 * YouTubePlayer — a de-branded YouTube embed.
 *
 * The goal is that an embedded YouTube video does NOT read as YouTube: no logo,
 * no title bar, no "Watch on YouTube", no related-video grid, and no
 * click-through to youtube.com. We achieve this by:
 *
 *   - Embedding via the privacy-preserving `youtube-nocookie.com` host.
 *   - Driving playback ourselves through the IFrame Player API with
 *     `controls=0` (plus rel=0, modestbranding=1, iv_load_policy=3, fs=0,
 *     disablekb=1, playsinline=1) so YouTube's own chrome never shows.
 *   - Rendering a CUSTOM control bar (play/pause, seek, time, volume,
 *     fullscreen) styled with the app's tokens.
 *   - Laying a transparent click-catcher over the iframe so a click toggles
 *     play/pause instead of opening youtube.com.
 *   - Covering the end-screen with our own replay overlay so YouTube's
 *     suggested-video grid is hidden.
 *
 * HONEST LIMIT: the underlying player is still YouTube's, so a determined user
 * can reach it (the iframe DOM, network requests to youtube-nocookie.com, and
 * the right-click "Copy video URL" native menu still exist). Visually, though,
 * it should not present as YouTube.
 */
export type YouTubePlayerProps = {
  videoId: string;
  className?: string;
  title?: string;
  /**
   * Слой поверх видео — водяной знак и всё, что должно остаться видимым
   * В ПОЛНОЭКРАННОМ РЕЖИМЕ. Разворачивается контейнер плеера, поэтому всё,
   * что навешено снаружи обёрткой, при развороте пропадает.
   */
  overlay?: ReactNode;
  /** Запустить сразу: человек уже нажал на обложку в уроке. */
  autoplay?: boolean;
  /**
   * Включать ли субтитры, когда СВОЕГО выбора на этом устройстве ещё нет.
   *
   * ⚠️ НУЖНО ТОЛЬКО СТРАНИЦЕ `/embed`. В приложении из магазина ролик уходит в
   * отдельное окно на нашем домене, а это ДРУГОЙ источник: хранилище главного
   * окна (`capacitor://localhost`) туда не видно. Выбор ученика приезжает
   * параметром адреса и служит лишь умолчанием — как только человек нажмёт
   * кнопку в самом окне, побеждает его хранилище.
   */
  captionsDefault?: boolean;
  /**
   * Скорость, когда СВОЕГО выбора на этом устройстве ещё нет.
   * Приезжает параметром адреса — как и `captionsDefault`, и ровно по той же
   * причине: у окна `/embed` свой источник и своё хранилище.
   */
  speedDefault?: number;
  /**
   * Разворот делает НЕ плеер, а тот, кто его показывает (16.09.2026, проба 60).
   *
   * ⚠️ НУЖНО ТОЛЬКО СТРАНИЦЕ `/embed` ВНУТРИ ПРИЛОЖЕНИЯ (`?native=1`). Там ролик живёт в отдельном нативном окне
   * размером с карточку урока: и системный разворот, и наш слой растянулись бы на ту же карточку. Кнопка просит
   * приложение развернуть само окно, значок берёт из `active`. Не передан — всё как было.
   */
  fullscreenControl?: { active: boolean; toggle: () => void };
};

const PLAYER_VARS = {
  rel: 0,
  modestbranding: 1,
  iv_load_policy: 3,
  playsinline: 1,
  controls: 0,
  disablekb: 1,
  fs: 0,
  color: 'white',
  // Hide annotations / keep the surface clean.
  showinfo: 0,
} as const;

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = String(total % 60).padStart(2, '0');
  return `${m}:${s}`;
}

/**
 * Включать ли субтитры при запуске: свой выбор, иначе умолчание извне.
 *
 * Считается ОДИН раз при монтировании — дальше состоянием правит кнопка.
 */
function initialCaptionsOn(fallback: boolean | undefined): boolean {
  if (captionsPrefStored()) return captionsPrefEnabled();
  return fallback ?? false;
}

/** То же правило для скорости: свой выбор важнее пришедшего извне умолчания. */
function initialSpeed(fallback: number | undefined): Speed {
  if (speedPrefStored()) return speedPref();
  return normalizeSpeed(fallback);
}

export function YouTubePlayer({
  videoId,
  className,
  title,
  overlay,
  autoplay,
  captionsDefault,
  speedDefault,
  fullscreenControl,
}: YouTubePlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mountRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const pollRef = useRef<number | null>(null);

  const [ready, setReady] = useState(false);
  // Тот же признак, но доступный из таймера: состояние внутри замыкания
  // остаётся тем, каким было при запуске эффекта.
  const readyRef = useRef(false);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(100);
  // Разворот (наш слой и системный) — `useContainerFullscreen` ниже, у `toggleFullscreen`.
  // Заказчик 07.09.2026: «почему в плеере нет кнопки на полный экран?» — телефон это восемь
  // учеников из десяти; кнопка есть всегда, где нет системного разворота — работает наш слой.
  // started — ролик хоть раз пошёл. До этого момента кадр YouTube показывает
  // СВОЮ заставку с названием, каналом и кнопкой «Смотреть на YouTube», и
  // никакие playerVars это не убирают: modestbranding YouTube отменил, а
  // showinfo убрал ещё раньше. Единственное надёжное средство — закрыть кадр
  // своей заставкой и снять её, когда видео уже играет.
  const [started, setStarted] = useState(false);
  // Крупная картинка есть не у всех роликов; при промахе падаем на hq, которая
  // существует всегда.
  const [posterFallback, setPosterFallback] = useState(false);

  // ---- Субтитры ----------------------------------------------------------
  const [ccOn, setCcOn] = useState(() => initialCaptionsOn(captionsDefault));
  // Тот же признак, что и `ccOn`, но видимый из обработчиков плеера: они
  // созданы один раз и держат состояние таким, каким оно было при запуске.
  const ccOnRef = useRef(ccOn);
  ccOnRef.current = ccOn;
  /**
   * Признак «плеер пересоздаётся ради субтитров, а не ради нового ролика».
   *
   * ⚠️ ПЕРЕСОЗДАНИЕ — ЕДИНСТВЕННЫЙ РАБОЧИЙ СПОСОБ. На живом плеере субтитры не
   * переключаются: `setOption('captions','track',…)` молча не срабатывает
   * (проверено в браузере 09.09.2026). Поэтому кнопка собирает плеер заново с
   * другими параметрами, а мы возвращаем ученика туда, где он был: время,
   * воспроизведение и снятую заставку.
   */
  const ccRecreateRef = useRef(false);
  /** Куда вернуться после пересоздания: секунда и играло ли видео. */
  const resumeAtRef = useRef(0);
  const resumePlayingRef = useRef(false);

  // ---- Скорость -----------------------------------------------------------
  const [speed, setSpeed] = useState<Speed>(() => initialSpeed(speedDefault));
  const [speedMenuOpen, setSpeedMenuOpen] = useState(false);

  // ---- Панель по касанию (телефон), правило — lib/playerPanel.ts ----------
  // ⚠️ На устройстве без наведения `group-hover` не срабатывает, а касание кадра
  // сразу ставило паузу: перемотать играющий ролик было нельзя (10.09.2026).
  const [panelShown, setPanelShown] = useState(false);
  const panelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showPanel = () => {
    setPanelShown(true);
    if (panelTimerRef.current) clearTimeout(panelTimerRef.current);
    panelTimerRef.current = setTimeout(() => setPanelShown(false), PANEL_SHOW_MS);
  };
  /** Касание самой панели — перемотка, скорость — продлевает показ. */
  const extendPanel = () => {
    if (panelShown) showPanel();
  };
  useEffect(
    () => () => {
      if (panelTimerRef.current) clearTimeout(panelTimerRef.current);
    },
    [],
  );
  // Виден из обработчиков плеера, созданных один раз при монтировании.
  const speedRef = useRef(speed);
  speedRef.current = speed;

  // ---- Create / destroy the player ---------------------------------------
  useEffect(() => {
    let cancelled = false;
    const host = mountRef.current;
    if (!host) return;

    setFailed(false);

    // YT.Player REPLACES the element we hand it with an <iframe>. Give it a
    // throwaway inner div so our stable `host` survives across re-creations.
    const inner = document.createElement('div');
    inner.style.width = '100%';
    inner.style.height = '100%';
    host.appendChild(inner);

    loadYouTubeIframeApi()
      .then((YT) => {
        if (cancelled) return;
        playerRef.current = new YT.Player(inner, {
          videoId,
          host: 'https://www.youtube-nocookie.com',
          width: '100%',
          height: '100%',
          // origin обязателен: без него плеер в приложении считает источник
          // подозрительным и молча отказывается играть.
          // Субтитры задаются ТОЛЬКО здесь, при сборке плеера: на живом
          // переключить их не удаётся. Кнопка поэтому пересобирает плеер.
          // `start` возвращает ученика на ту же секунду — иначе переключение
          // субтитров отбрасывало бы его в начало ролика.
          playerVars: {
            ...PLAYER_VARS,
            origin: window.location.origin,
            ...captionsPlayerVars(ccOnRef.current),
            ...(ccRecreateRef.current ? { start: Math.floor(resumeAtRef.current) } : {}),
          },
          events: {
            onReady: (e: YTPlayerEvent) => {
              if (cancelled) return;
              setReady(true);
              readyRef.current = true;
              // Скорость восстанавливаем ДО ветвления ниже: она принадлежит
              // ученику, а не ролику, и должна пережить и смену видео, и
              // пересоздание плеера ради субтитров.
              try {
                e.target.setPlaybackRate(speedRef.current);
              } catch {
                /* плеер откажет — останется обычная скорость, это не поломка */
              }
              // Возврат после пересоздания ради субтитров. `start` в параметрах
              // ставит секунду грубо (целые), поэтому доводим точно, и сами
              // продолжаем воспроизведение, если оно шло: для ученика нажатие
              // кнопки субтитров не должно быть похоже на перезапуск ролика.
              if (ccRecreateRef.current) {
                ccRecreateRef.current = false;
                try {
                  e.target.seekTo(resumeAtRef.current, true);
                  if (resumePlayingRef.current) e.target.playVideo();
                } catch {
                  /* редкая гонка при пересборке — ролик просто начнётся сначала */
                }
                setDuration(e.target.getDuration());
                setMuted(e.target.isMuted());
                setVolume(e.target.getVolume());
                return;
              }
              // Человек уже нажал на обложку в уроке — второй раз жать незачем.
              if (autoplay) {
                try {
                  e.target.playVideo();
                } catch {
                  /* запуск может отклонить система — тогда сработает кнопка */
                }
              }
              setDuration(e.target.getDuration());
              setMuted(e.target.isMuted());
              setVolume(e.target.getVolume());
            },
            onError: () => {
              if (!cancelled) setFailed(true);
            },
            onStateChange: (e: YTPlayerEvent) => {
              if (cancelled) return;
              const YTns = window.YT;
              const st = e.data;
              if (!YTns) return;
              if (st === YTns.PlayerState.PLAYING) {
                setPlaying(true);
                setStarted(true);
                setEnded(false);
                setDuration(e.target.getDuration());
              } else if (st === YTns.PlayerState.PAUSED) {
                setPlaying(false);
              } else if (st === YTns.PlayerState.ENDED) {
                setPlaying(false);
                setEnded(true);
              }
            },
          },
        });
      })
      .catch(() => {
        // API/player failed to load (blocked, offline, proxy, timeout).
        // Surface a visible fallback instead of a permanent dead play icon.
        if (!cancelled) setFailed(true);
      });

    // ⚠️ ОТКАЗ БЫВАЕТ И БЕЗ ОШИБКИ. В приложении плеер создавался, показывал
    // превью — и молча не оживал: время стояло на 0:00, нажатие ничего не
    // делало (заказчик 23.08.2026 «видосы не запускаются»). Ошибки при этом нет
    // ни одной: скрипт загрузился, объект создан, а обмена с YouTube не
    // произошло. Поэтому ждём готовности по часам: не ожил за шесть секунд —
    // считаем отказом и показываем запасной вариант.
    const deadline = window.setTimeout(() => {
      if (!cancelled) setFailed((was) => was || !playerRef.current || !readyRef.current);
    }, 6000);

    return () => {
      cancelled = true;
      window.clearTimeout(deadline);
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
      try {
        playerRef.current?.destroy();
      } catch {
        /* ignore teardown races */
      }
      playerRef.current = null;
      host.innerHTML = '';
      setReady(false);
      setPlaying(false);
      setEnded(false);

      // ⚠️ ПРИ ПЕРЕСОЗДАНИИ РАДИ СУБТИТРОВ ЭКРАН НЕ ОТКАТЫВАЕМ. Ролик тот же,
      // ученик никуда не уходил: сбрось мы здесь `started`, поверх кадра снова
      // легла бы заставка с обложкой. Время тоже оставляем: его вернёт `onReady`.
      if (ccRecreateRef.current) return;

      setStarted(false);
      setCurrent(0);
      setDuration(0);
    };
  }, [videoId, ccOn]);

  // ---- Poll current time while playing -----------------------------------
  useEffect(() => {
    if (!playing) {
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = window.setInterval(() => {
      const p = playerRef.current;
      if (!p) return;
      setCurrent(p.getCurrentTime());
      const d = p.getDuration();
      if (d && d !== duration) setDuration(d);
    }, 250);
    return () => {
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [playing, duration]);

  // ---- Controls ----------------------------------------------------------
  const togglePlay = () => {
    const p = playerRef.current;
    if (!p) return;
    if (ended) {
      p.seekTo(0, true);
      p.playVideo();
      setEnded(false);
      return;
    }
    if (playing) p.pauseVideo();
    else p.playVideo();
  };

  const onSeek = (value: number) => {
    const p = playerRef.current;
    if (!p) return;
    p.seekTo(value, true);
    setCurrent(value);
  };

  // ---- Перемотка −15 / +15 кнопками, правило — lib/playerSeek.ts (13.09.2026) ------
  // ⚠️ Двойного касания по краям кадра нет — решение владельца 13.09.2026: достаточно кнопок.

  /** Цель последнего шага: пока плеер её не принял, считаем от неё (lib/playerSeek.ts, `seekFrom`). */
  const lastSeekTargetRef = useRef<{ time: number; at: number } | null>(null);

  /**
   * Шаг назад или вперёд.
   *
   * ⚠️ ТОЧКУ ОТСЧЁТА ВЫБИРАЕТ `seekFrom`, А НЕ «ВСЕГДА ПЛЕЕР». Первая версия брала
   * время у плеера, и живая проверка на стенде 13.09.2026 это опровергла: у стоящего
   * ролика «−15» от 0:30 давало 0:00 — YouTube отвечал нулём.
   */
  const seekStep = (direction: -1 | 1) => {
    const p = playerRef.current;
    if (!p) return;
    let playerTime: number | null = null;
    try {
      playerTime = p.getCurrentTime();
    } catch {
      /* плеер ещё не готов — считаем от того, что знаем */
    }
    const now = Date.now();
    const from = seekFrom({ playing, playerTime, stateTime: current, lastTarget: lastSeekTargetRef.current, now });
    const next = seekBy(from, direction * SEEK_STEP_S, duration);
    p.seekTo(next, true);
    lastSeekTargetRef.current = { time: next, at: now };
    setCurrent(next);
    // Назад из конца ролика — снова обычный плеер, а не экран «Replay».
    if (ended && direction < 0) setEnded(false);
  };

  const toggleMute = () => {
    const p = playerRef.current;
    if (!p) return;
    if (p.isMuted()) {
      p.unMute();
      setMuted(false);
    } else {
      p.mute();
      setMuted(true);
    }
  };

  const onVolume = (value: number) => {
    const p = playerRef.current;
    if (!p) return;
    p.setVolume(value);
    setVolume(value);
    if (value === 0) {
      p.mute();
      setMuted(true);
    } else if (p.isMuted()) {
      p.unMute();
      setMuted(false);
    }
  };

  /**
   * Субтитры: включить или выключить.
   *
   * Выбор запоминается сразу — ученик включает субтитры один раз, а не в каждом
   * уроке. Состояние переставляем даже если плеер отказал: иначе кнопка
   * выглядела бы заевшей.
   */
  /**
   * ⚠️ КНОПКА НЕ ПРОВЕРЯЕТ, ЕСТЬ ЛИ У РОЛИКА СУБТИТРЫ, И НЕ МОЖЕТ ПРОВЕРИТЬ.
   * Список дорожек плеера отдаёт только опубликованные, а `cc_load_policy`
   * показывает и машинные — то есть «список пуст» не значит «субтитров нет».
   * Поэтому просто просим их у YouTube: где есть — покажутся.
   */
  const toggleCaptions = () => {
    const p = playerRef.current;
    // Запоминаем, куда возвращаться, ДО смены состояния: она пересоберёт плеер,
    // и спросить старый уже будет некого.
    if (p) {
      try {
        resumeAtRef.current = p.getCurrentTime();
      } catch {
        resumeAtRef.current = 0;
      }
      resumePlayingRef.current = playing;
      ccRecreateRef.current = true;
    }
    const next = !ccOn;
    setCcOn(next);
    setCaptionsPref(next);
  };

  /**
   * Скорость. Меняется на живом плеере — пересоздавать ничего не нужно.
   *
   * Состояние переставляем даже при отказе плеера: иначе кнопка выглядела бы
   * заевшей. Выбор запоминается сразу — он про ученика, а не про ролик.
   */
  const chooseSpeed = (next: Speed) => {
    setSpeed(next);
    setSpeedPref(next);
    setSpeedMenuOpen(false);
    try {
      playerRef.current?.setPlaybackRate(next);
    } catch {
      /* редкий отказ плеера — на следующем ролике применится в onReady */
    }
  };

  // Меню скорости закрывается по Esc и по нажатию мимо него. Без этого оно
  // остаётся висеть поверх кадра: панель сама прячется во время воспроизведения,
  // и открытое меню пережило бы её.
  useEffect(() => {
    if (!speedMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSpeedMenuOpen(false);
    };
    const onDown = (e: MouseEvent | TouchEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el?.closest('[data-speed-menu]')) setSpeedMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [speedMenuOpen]);

  /**
   * Разворот: системный там, где он есть, наш слой — где его нет.
   *
   * ⚠️ ПОЧЕМУ СИСТЕМНЫЙ ВЕРНУЛСЯ. Сначала мы отказались от него совсем: знак
   * пропадал в полном экране, и это дороже удобства. Но пропадал он не из-за
   * системного режима как такового — в полный экран уходил КАДР YOUTUBE по его
   * собственной кнопке, а знак лежит слоем снаружи кадра. Кнопку у YouTube мы
   * отобрали (параметр fs=0 и отсутствие разрешения на полный экран), и теперь
   * разворачивается НАШ контейнер, внутри которого и кадр, и знак. Проверять
   * это надо глазами: заказчик 07.09.2026 «в некоторых уходит, в некоторых
   * есть» — как раз про кадр против контейнера.
   *
   * ⚠️ ЗАПАСНОЙ ПУТЬ ОБЯЗАТЕЛЕН. На айфоне Safari разворачивает только сам
   * элемент video, а у нас видео в кадре — системного разворота там нет вовсе.
   * Раньше из-за этого кнопку просто прятали, и восемь учеников из десяти
   * (телефон) не могли увеличить видео. Теперь там работает наш слой.
   *
   * Отличить одно от другого по внешнему виду можно: у системного пропадает
   * строка браузера, у нашего остаётся.
   *
   * С 30.09.2026 сам разворот — `hooks/useContainerFullscreen.ts`: им же пользуется
   * видео файлом. Здесь остаётся только разворот силами окна приложения.
   */
  const { expanded, isFullscreen, rotated, toggle: toggleContainer } = useContainerFullscreen(containerRef);
  const toggleFullscreen = () => {
    // Разворачивает тот, кто показывает плеер (окно приложения) — см. `fullscreenControl`.
    if (fullscreenControl) {
      fullscreenControl.toggle();
      return;
    }
    toggleContainer();
  };

  const iconBtn =
    'grid h-9 w-9 place-items-center rounded-full text-white/90 transition hover:bg-white/15 hover:text-white';

  return (
    <div
      // «Играть» внутри задания — не начало ответа (окно «Выйти из урока?», lib/lessonAnswerGuard.ts).
      data-no-dirty=""
      ref={containerRef}
      className={cn(
        // yt-shell — зацепка для правила :fullscreen в index.css: в системном
        // полноэкранном режиме с контейнера снимается пропорция 16:9, иначе
        // видео остаётся полоской посреди чёрного экрана.
        'yt-shell group relative w-full overflow-hidden bg-black',
        expanded
          ? rotated
            ? 'fixed z-[70] rounded-none'
            : 'fixed inset-0 z-[70] h-full rounded-none'
          : 'aspect-video rounded-card',
        className,
      )}
      style={rotated}
    >
      {/* Наложение (водяной знак) рисуется ВНУТРИ контейнера, который и уходит
          в полноэкранный режим. Раньше знак вешали снаружи, обёрткой вокруг
          плеера, и при развороте он оставался за кадром — то есть исчезал
          ровно тогда, когда нужен: в полный экран и переходят, чтобы снять
          запись экрана. */}
      {overlay}
      {/* The IFrame Player mounts here. pointer-events off so the click-catcher
          fully governs interaction (no click-through to youtube.com). */}
      {/* Кадр YouTube растягивается СТИЛЯМИ, а не атрибутами.
          API проставляет width="100%" height="100%" атрибутами, а для iframe
          спецификация ждёт пиксели: браузер вправе их проигнорировать и
          оставить кадр своего размера. Тогда он не совпадает с контейнером,
          и по краям проступает плеер YouTube. Правило ниже задаёт размер
          жёстко, каким бы ни был атрибут. */}
      <div
        ref={mountRef}
        className="pointer-events-none absolute inset-0 h-full w-full
                   [&>iframe]:absolute [&>iframe]:inset-0 [&>iframe]:h-full [&>iframe]:w-full [&>iframe]:border-0"
      />

      {/* Прозрачный ловец нажатий. Компьютер: щелчок — пауза/пуск. Телефон:
          первое касание играющего ролика показывает панель и ролик не трогает,
          касание при видимой панели — пауза (lib/playerPanel.ts). */}
      <button
        type="button"
        aria-label={title ?? 'Toggle playback'}
        onClick={() => {
          const tap = frameTap({
            touch: typeof window.matchMedia === 'function' && window.matchMedia('(hover: none)').matches,
            playing,
            ended,
            panelShown,
          });
          if (tap.showPanel) showPanel();
          if (tap.togglePlay) togglePlay();
        }}
        onContextMenu={(e) => e.preventDefault()}
        className="absolute inset-0 z-10 h-full w-full cursor-pointer bg-transparent"
      />

      {/* Load failure fallback — a visible message + a plain link to open the
          video, instead of a permanent dead play icon. Solid surface layer on
          top (z-50) so it hides the mount + overlays underneath. */}
      {failed && (
        /* Обычная вставка YouTube: она не обменивается сообщениями со страницей
           и не смотрит на источник, поэтому играет и в приложении. Своё
           оформление здесь пропадает — зато ролик открывается, а это важнее. */
        /* ⚠️ ЗДЕСЬ НЕТ allowFullScreen, И ЭТО НЕ ЗАБЫТО.
           Именно этот запасной кадр объяснял, почему водяной знак пропадал «в
           некоторых» роликах, а в других оставался. В основном плеере кнопки
           полного экрана у YouTube нет (fs: 0), и разворачивается НАШ слой —
           знак поверх видео остаётся. А здесь показывались родные кнопки
           YouTube: по ним в полный экран уходил САМ КАДР, а знак лежит снаружи
           него — и пропадал. Заказчик 07.09.2026: «в некоторых уходит, в
           некоторых есть знак, странно».
           Полный экран отбираем в обоих местах: и параметром fs=0, и
           отсутствием разрешения. Одного параметра мало — он лишь прячет
           кнопку, а разрешение позволяет уйти в полный экран другими путями
           (двойной щелчок, клавиша F). */
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}?playsinline=1&rel=0&modestbranding=1&fs=0`}
          title={title || 'video'}
          allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
          className="absolute inset-0 z-50 h-full w-full border-0"
        />
      )}
      {false && (
        <div className="absolute inset-0 z-50 grid place-items-center bg-surface-container px-6 text-center">
          <div className="flex flex-col items-center gap-3">
            <span className="material-symbols-outlined text-4xl text-on-surface-variant">
              videocam_off
            </span>
            <p className="text-sm text-on-surface-variant">
              {title ? `${title} — ` : ''}
              This video could not be loaded here.
            </p>
            <a
              href={`https://www.youtube.com/watch?v=${videoId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-button bg-primary px-4 py-2 text-sm font-semibold text-on-primary transition hover:opacity-90"
            >
              <span className="material-symbols-outlined text-[18px]">open_in_new</span>
              Open video
            </a>
          </div>
        </div>
      )}

      {/* Своя заставка поверх кадра, пока ролик ни разу не запускали.
          Именно в этом состоянии YouTube рисует название, аватар канала и
          кнопку «Смотреть на YouTube» — то самое, ради чего плеер и делался
          безымянным. Слой НЕПРОЗРАЧНЫЙ и лежит выше кадра, но ниже ловца
          нажатий, поэтому клик по-прежнему запускает видео. */}
      {!failed && !started && (
        <div className="pointer-events-none absolute inset-0 z-[5] bg-black">
          <img
            src={
              posterFallback
                ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
                : `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`
            }
            alt=""
            aria-hidden
            onError={() => setPosterFallback(true)}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-black/25" />
        </div>
      )}

      {/* Кнопка запуска: до старта — поверх заставки, после — над кадром. */}
      {!failed && !playing && !ended && (
        <div className="pointer-events-none absolute inset-0 z-20 grid place-items-center">
          <span className="grid h-16 w-16 place-items-center rounded-full bg-black/45 text-white backdrop-blur-sm">
            <span className="material-symbols-outlined text-4xl">play_arrow</span>
          </span>
        </div>
      )}

      {/* End-screen overlay — hides YouTube's suggested-video grid. */}
      {ended && (
        <div className="absolute inset-0 z-30 grid place-items-center bg-black/80">
          <button
            type="button"
            onClick={togglePlay}
            className="flex flex-col items-center gap-2 text-white transition hover:opacity-80"
          >
            <span className="grid h-16 w-16 place-items-center rounded-full bg-white/15">
              <span className="material-symbols-outlined text-4xl">replay</span>
            </span>
            <span className="text-sm font-semibold">Replay</span>
          </button>
        </div>
      )}

      {/* Custom control bar. */}
      <div
        className={cn(
          // Затемнение, а не заливка. Непрозрачные полосы сверху и снизу
          // действительно прятали обвязку YouTube на паузе, но заодно съедали
          // четверть кадра: ученик ставит на паузу, чтобы разглядеть слайд, —
          // и упирается в чёрный прямоугольник. Решено оставить логотип
          // видимым: он мешает меньше, чем закрытый кадр.
          'absolute inset-x-0 bottom-0 z-40 flex flex-col gap-1 bg-gradient-to-t from-black/85 via-black/45 to-transparent px-3 pb-2 pt-10',
          // Hidden by default; pointer-events off while hidden so the invisible
          // bar doesn't intercept clicks over the bottom strip.
          'opacity-0 transition-opacity duration-200 group-hover:opacity-100 focus-within:opacity-100',
          'pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto',
          (!playing || ended) && 'pointer-events-auto opacity-100',
          // Показ после касания на телефоне — там наведения нет.
          panelShown && 'pointer-events-auto opacity-100',
        )}
        onPointerDown={extendPanel}
      >
        {/* Seek / progress */}
        <input
          type="range"
          min={0}
          max={Math.max(duration, 0.1)}
          step={0.1}
          value={Math.min(current, duration || 0)}
          onChange={(e) => onSeek(Number(e.target.value))}
          disabled={!ready}
          aria-label="Seek"
          className="h-1 w-full cursor-pointer appearance-none rounded-full bg-white/25 accent-primary"
        />

        <div className="flex items-center gap-1 text-white">
          <button type="button" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'} className={iconBtn}>
            <span className="material-symbols-outlined text-[22px]">
              {ended ? 'replay' : playing ? 'pause' : 'play_arrow'}
            </span>
          </button>

          {/* Перемотка на 15 секунд. Подпись ЦИФРАМИ, как у скорости: значков
              `replay_10` / `forward_10` в урезанном шрифте нет (iconsInFont.json),
              а английские aria-label — потому что страница `/embed` собирается без
              переводов. Размер — как у соседних кнопок ряда. */}
          <button
            type="button"
            onClick={() => seekStep(-1)}
            disabled={!ready}
            aria-label={`Back ${SEEK_STEP_S} seconds`}
            className={cn(iconBtn, 'w-auto px-2 text-xs font-semibold tabular-nums disabled:opacity-40')}
          >
            −{SEEK_STEP_S}
          </button>
          <button
            type="button"
            onClick={() => seekStep(1)}
            disabled={!ready}
            aria-label={`Forward ${SEEK_STEP_S} seconds`}
            className={cn(iconBtn, 'w-auto px-2 text-xs font-semibold tabular-nums disabled:opacity-40')}
          >
            +{SEEK_STEP_S}
          </button>

          <button type="button" onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'} className={iconBtn}>
            <span className="material-symbols-outlined text-[22px]">
              {muted || volume === 0 ? 'volume_off' : volume < 50 ? 'volume_down' : 'volume_up'}
            </span>
          </button>

          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={muted ? 0 : volume}
            onChange={(e) => onVolume(Number(e.target.value))}
            aria-label="Volume"
            className="hidden h-1 w-20 cursor-pointer appearance-none rounded-full bg-white/25 accent-primary sm:block"
          />

          <span className="ml-1 select-none text-xs font-medium tabular-nums text-white/90">
            {formatTime(current)} / {formatTime(duration)}
          </span>

          {/* Субтитры — кнопка есть ВСЕГДА.

              ⚠️ СНАЧАЛА ЕЁ ПОКАЗЫВАЛИ ТОЛЬКО ПРИ НЕПУСТОМ СПИСКЕ ДОРОЖЕК, И ЭТО
              БЫЛО ОШИБКОЙ. «Список пуст» приравняли к «субтитров нет» — а это
              разные вещи: список отдаёт ТОЛЬКО опубликованные дорожки, тогда
              как `cc_load_policy` показывает и машинные. Заказчик 09.09.2026
              прислал кадр с идущими субтитрами и вопросом «а почему тут нет
              кнопок?» — ролик `W8dPicyHvWw`, дорожка машинная, список пуст.
              Выходило худшее из возможного: субтитры идут (выбор ученика
              сохранён), а выключить их нечем.

              Узнать заранее, есть ли у ролика ХОТЬ КАКИЕ-ТО субтитры, через этот
              интерфейс нельзя. Поэтому кнопка безусловна: на 127 роликах из 131
              она работает, на 4 без субтитров вовсе — нажмётся впустую. Это
              дешевле, чем запертые субтитры на 104 роликах.

              ⚠️ ПОДПИСЬ ПО-АНГЛИЙСКИ НАМЕРЕННО, как у соседних кнопок этого
              файла. Плеер используется страницей `/embed` — отдельной точкой
              сборки без переводов вовсе (там только React и плеер), и
              `useTranslation` показал бы в приложении сырые ключи. */}
          <div className="ml-auto flex items-center gap-1">
            {/* Скорость. Подпись ЦИФРАМИ, а не значком, по двум причинам:
                значка скорости нет в вырезанном шрифте (там 256 знаков, и
                `speed` в их число не входит — вместо картинки вышло бы слово),
                а цифры вдобавок одинаково читаются на всех трёх языках, что
                важно для страницы `/embed` — она собирается без переводов. */}
            <div className="relative" data-speed-menu>
              {speedMenuOpen && (
                <div
                  className="absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 overflow-hidden
                             rounded-card bg-black/90 py-1 shadow-ambient backdrop-blur-sm"
                  role="menu"
                >
                  {SPEEDS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      role="menuitemradio"
                      aria-checked={s === speed}
                      onClick={() => chooseSpeed(s)}
                      className={cn(
                        'block w-full px-4 py-1.5 text-left text-sm tabular-nums transition',
                        s === speed
                          ? 'bg-primary font-semibold text-on-primary'
                          : 'text-white/80 hover:bg-white/15 hover:text-white',
                      )}
                    >
                      {speedLabel(s)}
                    </button>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => setSpeedMenuOpen((v) => !v)}
                aria-label="Playback speed"
                aria-haspopup="menu"
                aria-expanded={speedMenuOpen}
                className={cn(
                  iconBtn,
                  'w-auto px-2 text-xs font-semibold tabular-nums',
                  speed !== 1 && 'bg-primary text-on-primary hover:bg-primary hover:text-on-primary',
                )}
              >
                {speedLabel(speed)}
              </button>
            </div>

            {/* ⚠️ СОСТОЯНИЕ ВИДНО ТРЕМЯ ПРИЗНАКАМИ СРАЗУ, И ЭТО НЕ ИЗБЫТОК.
                Сначала включённость показывалась лишь чуть более светлым фоном
                (`bg-white/20`) — заказчик 09.09.2026: «непонятно по кнопке,
                субтитры включены или нет». На кадре, который сам по себе то
                светлый, то тёмный, полупрозрачная подложка не читается вовсе.
                Теперь: заливка акцентным цветом, другой значок и подчёркивание
                под кнопкой — как в родном плеере YouTube. */}
            <button
              type="button"
              onClick={toggleCaptions}
              aria-label={ccOn ? 'Subtitles on' : 'Subtitles off'}
              aria-pressed={ccOn}
              className={cn(
                iconBtn,
                'relative',
                ccOn
                  ? 'bg-primary text-on-primary hover:bg-primary hover:text-on-primary'
                  : 'text-white/70',
              )}
            >
              {/* ⚠️ Именно `closed_caption_off`, а не `_disabled`: шрифт значков
                  вырезанный (256 знаков), и второго в нём нет — на экране вместо
                  значка появилось бы слово. Поймано `iconSubset.test.ts`. */}
              <span className="material-symbols-outlined text-[22px]">
                {ccOn ? 'closed_caption' : 'closed_caption_off'}
              </span>
              {ccOn && (
                <span className="absolute inset-x-1.5 bottom-0.5 h-0.5 rounded-full bg-on-primary" />
              )}
            </button>
            {/* Кнопка есть ВСЕГДА: где нет системного разворота, работает наш. */}
            <button
              type="button"
              onClick={toggleFullscreen}
              aria-label="Fullscreen"
              className={iconBtn}
            >
              <span className="material-symbols-outlined text-[22px]">
                {expanded || isFullscreen || fullscreenControl?.active ? 'fullscreen_exit' : 'fullscreen'}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
